import {mapHandrailLength, mapRailingLength, parseInches} from './product-configurator-core.js';

const pendingCustomLengths = new Map();

if (!customElements.get('product-configurator')) {
  customElements.define(
    'product-configurator',
    class ProductConfigurator extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('[data-configurator-range]');
        this.output = this.querySelector('[data-configurator-output]');
        this.unavailableMessage = this.querySelector('[data-unavailable-message]');
        this.variants = this.readProductVariants();
        const initialValue = pendingCustomLengths.get(this.productKey) ?? this.range?.value;

        this.range?.addEventListener('input', () => this.update(this.range.value, false));
        this.range?.addEventListener('change', () => this.update(this.range.value, true));
        this.querySelector('[data-custom-length-open]')?.addEventListener('click', () => this.openCustomLengthDialog());
        this.querySelector('[data-custom-length-close]')?.addEventListener('click', () => this.closeCustomLengthDialog());
        this.querySelector('[data-custom-length-apply]')?.addEventListener('click', () => this.applyCustomLength());
        this.querySelector('[data-custom-length-input]')?.addEventListener('input', (event) => {
          this.updateCustomLengthButton(event.target.value);
          this.validateCustomLengthInput(event.target, false);
        });
        this.querySelector('[data-custom-length-dialog]')?.addEventListener('click', (event) => {
          if (event.target === event.currentTarget) this.closeCustomLengthDialog();
        });
        if (this.range && initialValue !== undefined) this.range.value = initialValue;
        this.updateScaleLabels();
        this.update(initialValue, false);
      }

      update(value, shouldDispatch) {
        const inches = parseInches(value);
        if (this.output) this.output.textContent = this.formatLength(inches);

        const mapped = this.mapChoice(inches);
        this.unavailableMessage?.toggleAttribute('hidden', mapped?.available === true);
        const customLengthProperty = this.customLengthProperty;
        customLengthProperty?.toggleAttribute('disabled', !mapped?.available);

        if (!mapped) {
          if (customLengthProperty) customLengthProperty.value = '';
          this.setAvailabilityState('invalid');
          return;
        }

        pendingCustomLengths.set(this.productKey, inches);
        mapped.input.checked = true;
        if (customLengthProperty) {
          customLengthProperty.value = '';
          if (mapped.available) customLengthProperty.value = inches.toString();
        }
        if (mapped.available) {
          this.setAvailabilityState('available');
        } else {
          this.setAvailabilityState('sold_out');
        }

        if (shouldDispatch) {
          mapped.input.dispatchEvent(new Event('change', {bubbles: true}));
        }
      }

      openCustomLengthDialog() {
        const dialog = this.querySelector('[data-custom-length-dialog]');
        const input = this.querySelector('[data-custom-length-input]');
        const apply = this.querySelector('[data-custom-length-apply]');
        if (!dialog || !input) return;

        input.value = this.range?.value || '';
        this.updateCustomLengthButton(input.value);
        this.validateCustomLengthInput(input, false);
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        input.focus();
      }

      closeCustomLengthDialog() {
        const dialog = this.querySelector('[data-custom-length-dialog]');
        if (!dialog) return;

        if (typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
      }

      applyCustomLength() {
        const input = this.querySelector('[data-custom-length-input]');
        if (!this.validateCustomLengthInput(input, true)) return;
        const inches = parseInches(input?.value);
        if (inches === null || !this.range) return;

        this.range.value = inches;
        this.update(inches, true);
        this.closeCustomLengthDialog();
        const form = this.closest('product-info')?.productForm?.querySelector('form');
        if (form) setTimeout(() => form.requestSubmit(), 0);
      }

      updateCustomLengthButton(value) {
        const apply = this.querySelector('[data-custom-length-apply]');
        const mapped = this.mapChoice(parseInches(value));
        const price = this.formatMoney(mapped?.variantPrice);
        if (apply && price) apply.textContent = `Add to Cart · ${price}`;
      }

      validateCustomLengthInput(input, shouldReport) {
        if (!input) return false;

        const inches = parseInches(input.value);
        const min = Number(input.min);
        const max = Number(input.max);
        const mapped = inches !== null && inches >= min && inches <= max ? this.mapChoice(inches) : null;
        const valid = mapped?.available === true;
        const message = valid ? '' : input.dataset.customLengthError || '';
        input.setCustomValidity(message);
        input.toggleAttribute('aria-invalid', !valid);

        const errorMessage = this.querySelector('[data-custom-length-error-message]');
        if (errorMessage) {
          errorMessage.textContent = message;
          errorMessage.toggleAttribute('hidden', valid);
        }

        const apply = this.querySelector('[data-custom-length-apply]');
        if (apply) apply.disabled = !valid;
        if (!valid && shouldReport) input.reportValidity();
        return valid;
      }

      formatMoney(cents) {
        if (!Number.isFinite(cents)) return '';
        if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
          return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
        }

        const currency = this.closest('variant-selects')?.dataset.currencyCode || 'USD';
        return new Intl.NumberFormat(undefined, {style: 'currency', currency}).format(cents / 100);
      }

      updateScaleLabels() {
        const minLabel = this.querySelector('[data-length-min-label]');
        const maxLabel = this.querySelector('[data-length-max-label]');
        if (minLabel) minLabel.textContent = this.formatLength(this.range?.min);
        if (maxLabel) maxLabel.textContent = this.formatLength(this.range?.max);
      }

      formatLength(value) {
        const inches = parseInches(value);
        if (inches === null) return '';
        if (this.dataset.configuratorDisplayUnit !== 'feet') return `${inches} in`;

        const feet = Math.floor(inches / 12);
        const remainder = inches % 12;
        if (feet === 0) return `${remainder} in`;
        if (remainder === 0) return `${feet} ft`;
        return `${feet} ft ${remainder} in`;
      }

      mapChoice(inches) {
        if (inches === null) return null;

        const choices = this.choices;
        if (this.dataset.configuratorKind === 'railing_length') return mapRailingLength(inches, choices);
        if (this.dataset.configuratorKind === 'handrail_length') return mapHandrailLength(inches, choices);
        return null;
      }

      get choices() {
        return Array.from(this.querySelectorAll('[data-option-value-id]')).map((input) => ({
          input,
          id: input.dataset.optionValueId,
          available: input.dataset.available === 'true',
          value: input.dataset.valueLabel,
          minInches: Number(input.dataset.minInches),
          maxInches: Number(input.dataset.maxInches),
          sizeInches: Number(input.dataset.sizeInches),
          variantPrice: this.variantForChoice(input)?.price,
        }));
      }

      variantForChoice(input) {
        const variantSelects = this.closest('variant-selects');
        const selected = new Map();
        variantSelects?.querySelectorAll('[data-option-name]:checked').forEach((option) => {
          selected.set(option.dataset.optionName, option.dataset.valueLabel || option.value);
        });
        variantSelects?.querySelectorAll('select').forEach((select) => {
          const option = select.selectedOptions[0];
          if (option?.dataset.optionName) selected.set(option.dataset.optionName, option.value);
        });
        selected.set(input.dataset.optionName, input.dataset.valueLabel || input.value);
        const values = Array.from(selected.values());

        return this.variants.find((variant) => values.every((value) => variant.options?.includes(value)));
      }

      setAvailabilityState(state) {
        const productForm = this.closest('product-info')?.productForm;
        const submitButton = productForm?.submitButton;
        productForm?.setAttribute('data-availability-state', state);
        submitButton?.setAttribute('data-availability-state', state);

        if (state === 'available') {
          this.toggleSubmitButton(false);
        } else if (state === 'sold_out') {
          this.toggleSubmitButton(true, window.variantStrings?.soldOut || 'Sold out');
        } else {
          this.toggleSubmitButton(true, window.variantStrings?.unavailable || 'Unavailable');
        }
      }

      toggleSubmitButton(disable, text) {
        this.closest('product-info')?.productForm?.toggleSubmitButton(disable, text);
      }

      get customLengthProperty() {
        return this.closest('product-info')?.querySelector('[data-custom-length-property]');
      }

      get productKey() {
        const productId = this.closest('variant-selects')?.dataset.productId || this.closest('product-info')?.dataset.productId || 'product';
        return `${productId}:${this.dataset.configuratorKind}`;
      }

      readProductVariants() {
        try {
          return JSON.parse(this.closest('variant-selects')?.querySelector('[data-product-variants]')?.textContent || '[]');
        } catch {
          return [];
        }
      }
    }
  );
}
